import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { store } from 'store';
import moment from 'moment';
import sortBy from 'lodash.sortby';
import {
  Button,
  CardList,
  Dropdown,
  IconList,
  Modal,
  Offline,
  BannerComponent,
  LevelNav,
} from 'common/components';
import { ALL_SORTED_ACTIVITIES, ACTIVITY_TYPES } from 'common/constants';
import { offline as OfflineIcon } from 'common/svg/icons';
import { capitalizeString, groupBy, sortByKey } from 'common/utils';
import * as constants from 'common/constants';
import { base64url_encode } from 'util/base64';
import { toCommaNumber } from 'common/formatters';
import { withApollo } from 'react-apollo';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
  FETCH_ALL_ACTIVITIES_WITH_FIELDS,
  FETCH_DELIVERY_ACTIVITY_WITH_FIELDS,
  FETCH_HARVESTING_ACTIVITY_WITH_FIELDS,
  FETCH_STORAGE_ACTIVITY_WITH_FIELDS,
} from 'api/activity';

// empty message changes depending on where the list appears
const DEFAUL_MESSAGES = {
  contract: 'Add an activity to this contract.',
  storage: 'Add an activity to this storage.',
  field: 'Add an activity to this field.',
  activity: 'Add custom activities for anything else you need to track.',
};

// define which route to go when add activity button is clicked
const ACTIVITY_ROUTE_BY_TYPE = {
  contract: 'DELIVERY',
  storage: 'STORAGE',
};

export class ActivityList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      filter: null,
      showUnavailableModal: false,
      isOnTop: false,
    };
  }

  componentDidMount() {
    const target = this._getTarget();
    target && target.addEventListener('scroll', this._listenToScroll);
  }

  componentWillUnmount() {
    const target = this._getTarget();
    target && target.removeEventListener('scroll', this._listenToScroll);
  }

  _listenToScroll = () => {
    const target = this._getTarget();
    if (target.scrollTop === 0 || target.pageYOffset === 0) {
      this.setState({
        isOnTop: true,
      });
    } else {
      if (this.state.isOnTop == true) {
        this.setState({
          isOnTop: false,
        });
      }
    }
  };

  _getTarget = () => {
    const { browser } = this.props;
    if (browser) {
      if (browser.is.mobile) {
        return window;
      }
      return document.querySelector('.field-wrapper__aside');
    }
    return null;
  };

  hideUnavailableModal = () => {
    this.setState({
      showUnavailableModal: false,
    });
  };

  showUnavailableModal = () => {
    this.setState({
      showUnavailableModal: true,
    });
  };

  // used to display multiple fertilizers in array
  _arrayToString(arr) {
    return (
      arr &&
      arr.reduce((acc, obj, i) => {
        if (i < 2) {
          if (arr.length === 1 || (i === 1 && arr.length == 2)) {
            return (acc += obj.name);
          } else if (i === 1 && arr.length > 2) {
            return (acc += `${obj.name}...`);
          } else {
            return (acc += `${obj.name}, `);
          }
        } else {
          return acc;
        }
      }, '')
    );
  }

  _getContentData(activity) {
    const {
      crop,
      harvesting,
      destination,
      destinations,
      activityType,
      fieldActivityType,
      fieldName,
      source,
      chemicals,
      fertilizing,
    } = activity;
    const list = [];
    let data = harvesting || destinations;

    let totalAmount =
      data &&
      [].concat(data).reduce((size, current) => {
        return size + current.amount.size;
      }, 0);

    switch (activityType) {
      // Storage activities
      case ACTIVITY_TYPES.STORAGE:
        if (this.props.displayAlternateStorageCard) {
          const storageId = this.props.id;
          const removingCrops = storageId === source.id;
          let label = destinations[0].destination.name;

          if (removingCrops) {
            label =
              destinations &&
              [].concat(destinations).reduce((currentString, destination) => {
                return currentString.concat(
                  `${destination.destination.name}, `
                );
              }, '');
          } else {
            // Multi-destinations
            totalAmount = activity.destinations.filter(destination => {
              if (destination.destination.id === storageId) {
                return destination.amount.size;
              }
            })[0].amount.size;
          }

          list.push({
            color: removingCrops ? 'red' : 'green',
            icon: removingCrops ? 'minus' : 'plus',
            label: totalAmount === 1 ? '1 Bushel' : `${totalAmount} Bushels`,
          });
          if (fieldActivityType === ACTIVITY_TYPES.HARVESTING) {
            !!fieldName &&
              list.push({
                icon: 'fields',
                label: fieldName,
              });
          }

          list.push({
            icon: 'storagefill',
            label: removingCrops ? label.substr(0, label.length - 2) : label, // Remove space & apostrophe from end
          });
        } else {
          list.push({
            icon: 'storagefill',
            label: source.name,
          });
          crop &&
            list.push({
              icon: 'crop',
              label: `${crop.name} ${crop.variety ? crop.variety : ''}`,
            });
        }
        break;

      // Delivery activities
      case ACTIVITY_TYPES.DELIVERY:
        destination &&
          list.push({
            icon: 'contractsgrey',
            label: activity.destination.destination.name,
          });
        crop &&
          list.push({
            icon: 'crop',
            label: `${crop.name} ${crop.variety ? crop.variety : ''}`,
          });
        break;

      case 'FIELD':
        !!fieldName &&
          list.push({
            icon: 'fields',
            label: fieldName,
          });
        switch (fieldActivityType) {
          // Harvesting activities
          case ACTIVITY_TYPES.HARVESTING:
            crop &&
              list.push({
                icon: 'crop',
                label: `${crop.name} ${crop.variety ? crop.variety : ''}`,
              });
            break;

          // Scouting activities
          case ACTIVITY_TYPES.SCOUTING:
            break;

          // Fertilizing activities
          case ACTIVITY_TYPES.FERTILIZING:
            fertilizing &&
              fertilizing.length > 0 &&
              list.push({
                icon: 'fertilizer',
                label: this._arrayToString(fertilizing),
              });
            break;

          // Spraying activities
          case ACTIVITY_TYPES.SPRAYING:
            chemicals &&
              chemicals.length > 0 &&
              list.push({
                icon: 'spray',
                label: this._arrayToString(chemicals),
              });
            break;

          case ACTIVITY_TYPES.SEEDING: {
            crop &&
              list.push({
                icon: 'crop',
                label: `${crop.name} ${crop.variety ? crop.variety : ''}`,
              });
            break;
          }
          default:
            if (harvesting && harvesting.length) {
              list.push({
                icon: 'storagefill',
                label: `${toCommaNumber(totalAmount)} ${capitalizeString(
                  harvesting[0].amount.type
                )}`,
              });
            }
            break;
        }
        break;

      default:
        break;
    }

    return (
      <Fragment>
        <IconList list={list} />
        {this.state.showUnavailableModal && (
          <Modal
            header={'Unavailable offline'}
            theme={['narrow']}
            onToggle={this.hideUnavailableModal}
          >
            <p>You must be connected to the internet to view this activity.</p>
            <section className={'field__group'}>
              <fieldset className={'field text--center'}>
                <Button
                  className="btn btn--primary"
                  onClick={this.hideUnavailableModal}
                >
                  OK
                </Button>
              </fieldset>
            </section>
          </Modal>
        )}
      </Fragment>
    );
  }

  _getCardData = activity => {
    const { client, season } = this.props;

    let obj;
    const {
      fieldActivityType,
      activityType,
      activityDate,
      destination,
      id,
      harvesting,
      destinations,
      finishDate,
    } = activity;
    let data = harvesting || destinations;
    const { type, inDetails } = this.props;
    let totalAmount;
    if (activityType === ACTIVITY_TYPES.DELIVERY) {
      totalAmount = destination.amount.size;
    } else {
      totalAmount =
        data &&
        [].concat(data).reduce((size, current) => {
          return size + current.amount.size;
        }, 0);
    }

    let availableOffline = false;
    let query = null;
    const variables = { active: true };
    if (activityType === ACTIVITY_TYPES.STORAGE) {
      query = FETCH_STORAGE_ACTIVITY_WITH_FIELDS;
      variables.id = id;
    } else if (
      activityType === 'FIELD' &&
      fieldActivityType === ACTIVITY_TYPES.HARVESTING
    ) {
      query = FETCH_HARVESTING_ACTIVITY_WITH_FIELDS;
      variables.id = id;
    } else if (activityType === ACTIVITY_TYPES.DELIVERY) {
      query = FETCH_DELIVERY_ACTIVITY_WITH_FIELDS;
      variables.id = id;
    } else {
      query = FETCH_ALL_ACTIVITIES_WITH_FIELDS;
      variables.season = season;
    }
    if (query != null) {
      try {
        client.readQuery({
          query,
          variables,
        });
        availableOffline = true;
      } catch (error) {
        // Apollo throws an error if no data is found
      }
    }

    if (this.props.displayAlternateStorageCard) {
      obj = {
        // top: <ShareIcon />, //hide for now
        sectionTitle: moment(activityDate).format('MMMM D YYYY'),
        content: this._getContentData(activity),
        link: `${constants.ROUTE_ACTIVITY_DETAIL}/${base64url_encode(
          activity.id.trim()
        )}`,
      };
      return obj;
    }

    obj = {
      sectionTitle: moment(activityDate).format('MMMM D YYYY'),
      status: this._getActivityStatus(activityDate, finishDate),
      availableOffline,
      handleUnavailableClick: this.showUnavailableModal,
      title:
        ((fieldActivityType === ACTIVITY_TYPES.HARVESTING ||
          activityType === ACTIVITY_TYPES.DELIVERY) &&
          ['field'].indexOf(type) < 0 &&
          inDetails &&
          `${totalAmount} Bushels`) ||
        (fieldActivityType || activityType),
      content: this._getContentData(activity),
      // top: <ShareIcon />, //hide for now
      link: `${constants.ROUTE_ACTIVITY_DETAIL}/${base64url_encode(id.trim())}`,
    };

    return obj;
  };

  _getActivityStatus = (activityDate, finishDate) => {
    if (finishDate) {
      return { text: 'Done', backgroundColor: '#006332' };
    }

    if (moment(activityDate).isBefore(moment().startOf('day'))) {
      return { text: 'Overdue', backgroundColor: '#BB4300' };
    }
    return { text: 'Planned', backgroundColor: '#EBA008' };
  };

  _filterActivities(activities) {
    const { filter } = this.state;
    return filter
      ? activities.filter(
          item =>
            item.fieldActivityType === filter || item.activityType === filter
        )
      : activities;
  }

  _setFilter(filter) {
    this.setState({ filter });
  }

  _getActivityTypeFilter = () => {
    const selected = this.state.filter || 'ALL';
    const list = ['ALL', ...ALL_SORTED_ACTIVITIES].filter(
      item => item !== selected
    );

    return (
      <Dropdown
        hideOnSelect
        trigger={<span>{selected}</span>}
        theme={['right', 'select']}
      >
        <ul>
          {list.map(
            item =>
              item && (
                <li
                  key={item}
                  onClick={() => this._setFilter(item === 'ALL' ? null : item)}
                >
                  {item}
                </li>
              )
          )}
        </ul>
      </Dropdown>
    );
  };

  _renderFilters = () => {
    const { isOnTop } = this.state;
    const theme = ['strong', 'sticky'];
    if (isOnTop) {
      theme.push('simple');
    }
    return (
      <BannerComponent theme={theme}>
        <LevelNav
          theme={['sticky']}
          nav={{
            right: {
              element: this._getActivityTypeFilter(),
            },
          }}
        />
      </BannerComponent>
    );
  };

  render() {
    const { field, type, activities, showActivityTypes } = this.props;
    // sort by finishDate
    const sorted =
      activities && activities.length > 1
        ? sortBy(activities.slice(), a => a.finishDate)
        : activities;
    const filtered = this._filterActivities(sorted);
    const mapped = filtered && filtered.map(this._getCardData);
    // group activities by sectionTitle
    const grouped =
      store.getState().settings.season === new Date().getFullYear() // only display today for current season
        ? {
            [moment().format('MMMM D YYYY')]: [],
            ...groupBy(mapped, 'sectionTitle'),
          }
        : groupBy(mapped, 'sectionTitle');

    // sort by object keys(activityDate)
    const sortedObj = sortByKey(
      grouped,
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    return (
      <Fragment>
        {!field && this._renderFilters()}
        <Offline>
          <BannerComponent
            message="Some activities may not be available in offline mode."
            theme={['offline-short']}
            leftIcon={<OfflineIcon />}
          />
        </Offline>
        <CardList
          section={{ data: sortedObj, scroll: true }}
          emptyStateconfig={{
            message: type ? DEFAUL_MESSAGES[type] : DEFAUL_MESSAGES.activity,
            btnMessage: 'Add Activity',
            routeName: constants.ROUTE_ACTIVITY_ADD_TYPE(
              ACTIVITY_ROUTE_BY_TYPE[type]
            ),
            showActivityTypes,
          }}
        />
      </Fragment>
    );
  }
}

ActivityList.propTypes = {
  client: PropTypes.object,
  field: PropTypes.any,
  season: PropTypes.number,
  activities: PropTypes.array,
  displayAlternateStorageCard: PropTypes.bool,
  id: PropTypes.string,
  type: PropTypes.string,
  inDetails: PropTypes.bool,
  showActivityTypes: PropTypes.bool,
  browser: PropTypes.object,
};

ActivityList.defaultProps = {
  activities: [],
  showActivityTypes: false,
};

const mapState = ({ settings: { season }, browser }) => ({
  season,
  browser,
});

export default compose(
  connect(mapState),
  withApollo
)(ActivityList);



// WEBPACK FOOTER //
// ./src/containers/ActivityList/index.js